
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";
import MDBox from "components/MDBox";

// Icons
import GroupIcon from "@mui/icons-material/Group"; // Audience
import TouchAppIcon from "@mui/icons-material/TouchApp"; // Engagement
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions"; // Sentiment
import ArticleIcon from "@mui/icons-material/Article"; // Content
import PersonPinIcon from "@mui/icons-material/PersonPin"; // Demographics
import DashboardIcon from "@mui/icons-material/Dashboard"; // Summary/Default

const getCategoryIcon = (category) => {
    const iconStyle = { color: "#ffffff" }; // Force white color
    switch (category?.toLowerCase()) {
        case "audience":
            return <GroupIcon sx={iconStyle} />;
        case "engagement":
            return <TouchAppIcon sx={iconStyle} />;
        case "sentiment":
            return <EmojiEmotionsIcon sx={iconStyle} />;
        case "content":
            return <ArticleIcon sx={iconStyle} />;
        case "demographics":
            return <PersonPinIcon sx={iconStyle} />;
        default:
            return <DashboardIcon sx={iconStyle} />;
    }
};

const SideNavPill = ({ categories }) => {
    const [activeCategory, setActiveCategory] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    // Scroll to section handler
    const handleScrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 240;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    // ScrollSpy Logic
    useEffect(() => {
        const handleScroll = () => {
            let current = null;
            categories.forEach((cat) => {
                const id = `section-${cat.replace(/\s+/g, '-').toLowerCase()}`;
                const element = document.getElementById(id);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 300 && rect.bottom >= 300) {
                        current = cat;
                    }
                }
            });
            if (current !== activeCategory) {
                setActiveCategory(current);
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [categories, activeCategory]);

    if (!categories || categories.length === 0) return null;

    return (
        <MDBox
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                position: "fixed",
                right: 24,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                alignItems: "center", // Center items horizontally
                gap: 2,
                backgroundColor: "#2e2e3e",
                padding: "16px 8px",
                borderRadius: "50px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Smooth width transition
                width: isHovered ? "56px" : "28px", // Expand/Shrink width
                overflow: "hidden"
            }}
        >
            {categories.map((cat) => {
                const isActive = activeCategory === cat;

                return (
                    <Tooltip key={cat} title={cat} placement="left" arrow>
                        <MDBox
                            onClick={() => handleScrollTo(`section-${cat.replace(/\s+/g, '-').toLowerCase()}`)}
                            sx={{
                                width: isHovered ? 40 : 12, // Icon size vs Dot size (container)
                                height: isHovered ? 40 : 12,
                                minHeight: isHovered ? 40 : 12, // Prevent collapse during transition
                                borderRadius: isHovered ? "12px" : "50%", // Rounded Rect vs Circle
                                backgroundColor: isActive ? "#00A1A1" : (isHovered ? "transparent" : "#cfcfcf"),
                                color: "#ffffff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                border: (!isHovered && isActive)
                                    ? "2px solid #00A1A1" // Dot Active: Border
                                    : "none",
                                boxShadow: (!isHovered && isActive)
                                    ? "0 0 0 2px #fff, 0 0 0 4px #00A1A1" // Dot Active: Ring
                                    : "none",
                                "&:hover": {
                                    backgroundColor: isActive ? "#00A1A1" : "rgba(255,255,255,0.1)",
                                },
                            }}
                        >
                            {isHovered ? (
                                // Show ICON when expanded
                                <MDBox
                                    component="span"
                                    sx={{
                                        display: "flex",
                                        animation: "fadeIn 0.3s ease-in",
                                        "@keyframes fadeIn": {
                                            "0%": { opacity: 0, transform: "scale(0.5)" },
                                            "100%": { opacity: 1, transform: "scale(1)" },
                                        }
                                    }}
                                >
                                    {getCategoryIcon(cat)}
                                </MDBox>
                            ) : (
                                // DOT (Invisible content, purely styled via MDBox above)
                                null
                            )}
                        </MDBox>
                    </Tooltip>
                );
            })}
        </MDBox>
    );
};

SideNavPill.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default SideNavPill;
